import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  ScriptOnce,
} from "@tanstack/react-router";
import { Toaster } from "sonner";

import appCss from "../styles.css?url";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Sidebar } from "@/components/layout/Sidebar";
import { useAppStore } from "@/lib/store";

function NotFoundComponent() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The calculator you're looking for doesn't exist.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
        >
          Go home
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Bangla Calculator — Free BD Calculators" },
      {
        name: "description",
        content:
          "Bangla Calculator (calculatorbangla.com) is a bilingual (English / বাংলা) suite of finance, health, math and Bangladesh-specific calculators.",
      },
      { name: "author", content: "Bangla Calculator" },
      { property: "og:title", content: "Bangla Calculator — Every calculator, built for Bangladesh" },
      { property: "og:description", content: "Bangla Calculator is a bilingual web app offering a comprehensive suite of calculators for Bangladeshi users." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://calculatorbangla.com" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Bangla Calculator — Every calculator, built for Bangladesh" },
      { name: "twitter:description", content: "Bangla Calculator is a bilingual web app offering a comprehensive suite of calculators for Bangladeshi users." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/c81f92da-697b-4e31-8dab-b488894ca20b/id-preview-09d20cd7--8d75f185-df6b-4b81-8864-33eee78d81f4.lovable.app-1778402113638.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/c81f92da-697b-4e31-8dab-b488894ca20b/id-preview-09d20cd7--8d75f185-df6b-4b81-8864-33eee78d81f4.lovable.app-1778402113638.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "apple-touch-icon", href: "/favicon.svg" },
    ],
    scripts: [
      {
        children:
          "(function(){var l=document.createElement('link');l.rel='stylesheet';l.href='https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Hind+Siliguri:wght@400;600&display=swap';document.head.appendChild(l);})();",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

const themeBootstrap = `(function(){try{var d=JSON.parse(localStorage.getItem("bangla-calc-prefs")||"null");var t=d&&d.state&&d.state.theme;var l=d&&d.state&&d.state.lang;if(t==="dark")document.documentElement.classList.add("dark");if(l)document.documentElement.lang=l;}catch(e){}})();`;

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <ScriptOnce>{themeBootstrap}</ScriptOnce>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function useDeferredThirdParty() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ((window as unknown as { __3pLoaded?: boolean }).__3pLoaded) return;

    const inject = () => {
      if ((window as unknown as { __3pLoaded?: boolean }).__3pLoaded) return;
      (window as unknown as { __3pLoaded?: boolean }).__3pLoaded = true;

      // Google Analytics (gtag)
      const gtagScript = document.createElement("script");
      gtagScript.async = true;
      gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=G-FP3YFT123D";
      document.head.appendChild(gtagScript);

      const gtagInit = document.createElement("script");
      gtagInit.text =
        "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-FP3YFT123D');";
      document.head.appendChild(gtagInit);

      // Google AdSense
      const adsScript = document.createElement("script");
      adsScript.async = true;
      adsScript.src =
        "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7418120910489416";
      adsScript.crossOrigin = "anonymous";
      document.head.appendChild(adsScript);
    };

    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (idle) idle(inject, { timeout: 4000 });
      else timer = setTimeout(inject, 2500);
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("load", schedule);
    };
  }, []);
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const autoDetectLang = useAppStore((s) => s.autoDetectLang);

  useEffect(() => {
    autoDetectLang();
  }, [autoDetectLang]);

  useDeferredThirdParty();

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <Header />
        <div className="mx-auto flex w-full max-w-7xl flex-1 gap-8 px-3 sm:px-4 md:px-6">
          <aside className="hidden lg:block w-64 shrink-0 py-8">
            <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-2">
              <Sidebar />
            </div>
          </aside>
          <main className="min-w-0 flex-1 overflow-x-clip">
            <Outlet />
          </main>
        </div>
        <Footer />
      </div>
      <Toaster position="bottom-right" richColors />
    </QueryClientProvider>
  );
}
